import { view } from "@risingstack/react-easy-state";
import { Chip } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { useCallback, useEffect } from "react";
import { sendFilter } from "../../common/tracking";
import type { BrandType, FilterSettings, LegalFilterKey } from "../../common/types/productTypes";
import { translateFilterValue } from "../../common/utils/filterUtils";
import productStore from "../../stores/product/productStore";
import { getBrandSort } from "../../stores/product/productUtils";
import theme from "../../themes/theme";
import BrandGroupChip from "./BrandGroupChip";

type FilterChipsProps = {
   filterSettings: FilterSettings;
   exclusive?: boolean;
   includeAll?: boolean;
   resetFilterFields?: LegalFilterKey[];
};

const FilterChips = ({ filterSettings, exclusive = false, includeAll = false, resetFilterFields = [] }: FilterChipsProps) => {
   const brandSort = useCallback(getBrandSort(theme.storeId), [theme.storeId]);
   const { field, values: optionsKeys } = filterSettings;
   const selected = productStore.activeFilters[field];
   const valuesString = selected ? selected.join(",") : "";
   useEffect(() => {
      const currentFilters = productStore.activeFilters[field];
      const ineligibleFilters = currentFilters?.filter((filter) => !optionsKeys.includes(filter));
      for (const filter of ineligibleFilters || []) {
         productStore.removeFilterValue(field, filter);
      }
   }, [valuesString, field, optionsKeys]);

   const fieldsToReset = exclusive
      ? resetFilterFields.includes(field)
         ? resetFilterFields
         : [...resetFilterFields, field]
      : resetFilterFields.filter((field) => field !== filterSettings.field);
   const chipClassName = "!tw-border tw-border-solid !tw-border-ink-border-subtle tw-text-sm";

   const setFilter = (option: string, isSet: boolean) => {
      if (isSet) {
         productStore.clearFilterFields(fieldsToReset);
         sendFilter(field, option);
         productStore.addFilterValue(field, option);
      } else {
         productStore.removeFilterValue(field, option);
      }
   };

   const keys = filterSettings.field === "brandGroup" ? brandSort(optionsKeys) : optionsKeys;

   const isNoneSelected = keys.every((key) => {
      const isSelected = productStore.isFilterActive(filterSettings.field, key);
      return !isSelected;
   });

   useEffect(() => {
      if (isNoneSelected || !optionsKeys.length) {
         productStore.clearFilterFields([filterSettings.field]);
      }
   }, [isNoneSelected, optionsKeys]);

   if (!optionsKeys.length) {
      return null;
   }

   return (
      <>
         {includeAll && (
            <Chip
               className={cn("tw-text-sm", chipClassName)}
               checked={isNoneSelected}
               onClick={() => {
                  productStore.clearFilterFields(fieldsToReset);
               }}
            >
               Alle
            </Chip>
         )}
         {keys.map((optionName) => {
            const name = translateFilterValue(filterSettings.field, optionName);
            const checked = productStore.isFilterActive(filterSettings.field, optionName);

            if (filterSettings.field === "brandGroup") {
               return (
                  <BrandGroupChip
                     key={optionName}
                     brandGroup={optionName as BrandType}
                     checked={checked}
                     onClick={() => setFilter(optionName, !checked)}
                     className="tw-text-sm"
                  >
                     {name}
                  </BrandGroupChip>
               );
            }

            return (
               <Chip
                  key={optionName}
                  className={cn("tw-text-sm", chipClassName)}
                  checked={checked}
                  onClick={() => setFilter(optionName, !checked)}
               >
                  {name}
               </Chip>
            );
         })}
      </>
   );
};

export default view(FilterChips);
